import { inject, Injectable, signal, computed, untracked } from '@angular/core';
import { ClockInOutResponse, Events } from '../interfaces/schedule.interface';
import { EventsPayloadV2 } from '../interfaces/scheadule-v2.interface';
import { FichaBuildService } from './ficha-build.service';
// import { FichaStatesBuilderService } from './ficha-states-builder.service';

import { FichaEventBuildService } from './ficha-event-build.service';
import { FichaSettings, EventStates } from '@fichaje/interfaces/ficha-event.interface';
import { EventNeo, EventsNeo, MetaNeo, OpenSubEvent, WorkerCurrent } from '@fichaje/interfaces/ficha.interface';
import { HttpFichaService } from './http-ficha.service';
import { FichaJsonBuildService } from './ficha-json-build.service';
import { FichaJsonBuildV2Service } from './ficha-json-build-v2.service';
import { EventNeoEmptyService } from './event-neo-empty.service';
import { InOut, LogicLinkAction } from '@fichaje/logic-dialogs-v2/components/logic-dialogs.interface';
import { SetEventTypeService } from './set-event-type.service';
import { FichaStatesBuilderService } from './ficha-states-builder.service';
import type { ActionEventsRefreshPayload } from '../adapters/action-events-compat.adapter';
import type { EventDatumV2 } from '../interfaces/scheadule-v2.interface';

@Injectable({
  providedIn: 'root'
})
export class FichaService {

  // Token de version de sesion/request para ignorar respuestas tardias de sesiones previas.
  private requestVersion = 0;

  private readonly eventNeoEmpty =      inject(EventNeoEmptyService)
  private readonly fichaHttp =          inject(HttpFichaService);
  private readonly fichaJSONbuilder =   inject(FichaJsonBuildService);
  private readonly fichaJSONbuilderV2 = inject(FichaJsonBuildV2Service);

  private readonly fichaBuild =         inject(FichaBuildService);
  private readonly fichaStatesBuilder = inject(FichaStatesBuilderService);
  private readonly setEventType =       inject(SetEventTypeService);
  private readonly fichaEventBuild =    inject(FichaEventBuildService);

  //* 0) - al llegar una fecha nueva, se resetean los estados, sea esta valida o no y se valida la fecha.
  validateDayStringISO(stringDayISO:string){
    this.updatePristine(true);
    this.updateEventExpandedId(undefined); // reset eventExpandedId
    // const validStringDayISO = this.fichaBuild.validateDayStringISO(stringDayISO);
    // if(!validStringDayISO)
    //   return undefined;
    // return validStringDayISO;
    return this.fichaBuild.validateDayStringISO(stringDayISO);
  }

  //* 1) - se crea una version inicial de SETTINGS ######################
  public readonly initialSettings$ = signal<FichaSettings | undefined>(undefined);
  public setInitialSettings(settings:any | undefined){
    const s = this.fichaBuild.buildInitialFichaSettings(
      settings.stringDayISO,
      settings.userLogged,
      settings.isStation,
      settings.readOnly,
    );
    // untracked: se llama desde effects; leer initialSettings$ aquí los haría re-ejecutarse en bucle.
    if (untracked(this.initialSettings$)?.userLogged?.id !== s.userLogged?.id)
      this.lastWorkerCurrentSignal.set(undefined);
    this.initialSettings$.set(s);
  }

  //* Último worker_current recibido: la cabecera lo mantiene mientras se cargan los eventos de otro día.
  private readonly lastWorkerCurrentSignal = signal<WorkerCurrent|undefined>(undefined);
  public readonly lastWorkerCurrent$ = this.lastWorkerCurrentSignal.asReadonly();

  public resetSessionState(){
    this.requestVersion++;
    this.initialSettings$.set(undefined);
    this.lastWorkerCurrentSignal.set(undefined);
    this.eventsNeoRespSignal.set(undefined);
    this.eventStatesSignal.set({isPristine:true});
  }

  //* 2) - eventsRaw translated to EventsNeo ##########
  private readonly eventsNeoRespSignal = signal<EventsNeo|undefined>(undefined);
  public readonly eventsResponse$ = this.eventsNeoRespSignal.asReadonly();
  public setEventsResp(events:EventsNeo|undefined){
    this.updateTeletrabajoSwitched(undefined); //! VALIDAR EL LUNES
    if (events?.meta?.worker_current) this.lastWorkerCurrentSignal.set(events.meta.worker_current);
    this.eventsNeoRespSignal.set(events);
  }

  public getEvents(initialSettings:FichaSettings = this.initialSettings$()!){
    if (!initialSettings) return undefined;
    const userLogged = initialSettings.userLogged;
    if (userLogged?.company_id === undefined || userLogged?.id === undefined) {
      console.error('FichaService.getEvents - userLogged incompleto, se aborta la carga', userLogged);
      this.setEventsResp(undefined);
      return undefined;
    }

    const requestVersion = ++this.requestVersion;
    // Evita mostrar eventos previos mientras llega la nueva carga del usuario/sesion activa.
    this.setEventsResp(undefined);

    const stringDayISO = initialSettings.stringDayISO;
    const companyId = userLogged.company_id;
    const type = 'work,others,it,holidays,dld,libre';
    const workerId = userLogged.id;
    const isStation = initialSettings.isStation

    this.fichaHttp.getEvents(companyId, stringDayISO, type, workerId, isStation).subscribe({
      next: (events) => {
        if (requestVersion !== this.requestVersion) return;

        // Auto-selección y persistencia de centros de trabajo
        const centros = events?.meta?.centros_trabajo;
        if (centros) {
          const cachedCentro = localStorage.getItem(`ctw:${workerId}:${stringDayISO}`);
          if (centros.length === 1) {
            this.selectedCentroUuidSignal.set(centros[0].uuid);
          } else if (cachedCentro && centros.some((c: any) => c.uuid === cachedCentro)) {
            this.selectedCentroUuidSignal.set(cachedCentro);
          } else {
            this.selectedCentroUuidSignal.set(null);
          }
        }

        //* Se traduce a EventsNeo y se agrega el evento phantom si hay hora extra activa
        const evNeo = this.eventsDataToNeoV2(events)
        this.setEventsResp(evNeo)
      },
      error: (error) => {
        if (requestVersion !== this.requestVersion) return;
        this.setEventsResp(undefined);
        console.error('Error getting HTTP FETCH EVENTS:', error);
        return
      }
    })
  }

  //* 3) - build final SETTINGS$ = initialSettings$ + eventsResponse$ ##########
  public readonly settings$ = computed<FichaSettings | undefined>( () => {
    // console.log('settings$ changein - step 3')
    return this.fichaBuild.buildSettings(this.initialSettings$(), this.eventsResponse$())
  })

  //* 4) - build 'eventsComplete$', includes META CLOSED EVENTS ################
  private readonly eventsComplete$ = computed( () =>
    this.fichaBuild.buildEventsBuild(this.eventsResponse$()));


  //* 5) - set 'eventsNeoTyped' INCLUDES EVENT TYPE OBJ
  public readonly eventsNeoTyped$ = computed( () =>
    this.setEventType.eventsNeoTyped(this.eventsComplete$()));


  //* 6) - build STATES$ = eventsNeoTyped$ + SETTINGS$ ##########
  public readonly states$ = computed( () => {
    if (!this.eventsNeoTyped$() || !this.settings$()) return
    return this.fichaStatesBuilder.buildFichajeStates(this.eventsNeoTyped$()!, this.eventsResponse$()?.meta!, this.settings$()!, this.eventStates$())
  })





  //* 7) - events2Html, events used in the HTML components = SETTINGS, STATES y 'eventsRaw' ########
  public readonly events2Html$ = computed( () => {
    const events2Html = this.fichaEventBuild.buildEvents2Html( this.eventsNeoTyped$(), this.settings$(), this.states$(), this.eventsResponse$()?.meta!)
    // console.log('FichajeService - settings$: ',       this.settings$() );
    // console.log('FichajeService - eventsResponse$: ', this.eventsResponse$() );
    // console.log('FichajeService - states$: ',         this.states$() );
    // console.log('FichajeService - eventsComplete$: ', this.eventsComplete$() );
    // console.log('FichajeService - eventsNeoTyped$: ', this.eventsNeoTyped$() );
    // console.log('FichajeService - events2Html$: ',    events2Html );
    return events2Html
  });


  // ########################################################################
  // EVENT STATES ###########################################################
  //! CANDIDATO A MOVERSE DENTRO DE FICHAJE COMPONENT

  private readonly eventStatesSignal = signal<EventStates>({isPristine:true});
  public readonly eventStates$ = this.eventStatesSignal.asReadonly();
  public updatePristine(pristine:boolean){
    this.eventStatesSignal.update(x => ({...x, isPristine:pristine}))
  }
  public updateEventExpandedId(id:string|undefined){
    this.eventStatesSignal.update(x => ({...x, eventExpandedId:id}))
  }
  public updateTeletrabajoSwitched(val:boolean|undefined){
    this.eventStatesSignal.update(x => ({...x, EventInProgressRemoteWorkSwitched:val}))
  }

  // ########################################################################
  // CENTRO TRABAJO STATE ###################################################

  private readonly selectedCentroUuidSignal = signal<string | null>(null);
  public readonly selectedCentroUuid$ = this.selectedCentroUuidSignal.asReadonly();

  public setSelectedCentro(uuid: string | null) {
    this.selectedCentroUuidSignal.set(uuid);
    const workerId = this.settings$()?.userLogged?.id;
    const stringDayISO = this.settings$()?.stringDayISO;
    if (workerId && stringDayISO) {
      if (uuid) {
        localStorage.setItem(`ctw:${workerId}:${stringDayISO}`, uuid);
      } else {
        localStorage.removeItem(`ctw:${workerId}:${stringDayISO}`);
      }
    }
  }



  // ########################################################################
  // HTTP FICHAJE ##########################################################

  buttonActionClicked(link:LogicLinkAction, inOut:InOut, scheduleId:number){
    // console.log('IN ACTION:', link);
    if (!inOut) return
    if(link.logicLinkType == 'actionClockInOut')
      console.log('fichaservice - actionClockInOut:', link) // this.setFichaje(link,inOut,scheduleId)
    else if(link.logicLinkType == 'actionTimeOffInOut')
      console.log('fichaservice - actionTimeOffInOut:', link) // this.setTimeOff(link,inOut,scheduleId)
    else if(link.logicLinkType == 'actionBreakInOut')
      console.log('fichaservice - actionBreakInOut:', link) // this.setBreak(link,inOut,scheduleId)
  }


  // ################################################################
  // ####################### HELPER FUNCTIONS #######################

  public eventsDataToNeo(events:Events|ClockInOutResponse|undefined, serverTime?:string){
    // Acepta respuestas mixtas (`Events` directo o `{ events: Events }`) y evita crasheos por payload parcial.
    const normalizedEvents = this.normalizeEventsPayload(events);
    const neoJSON = this.fichaJSONbuilder.eventsNeo(normalizedEvents)
    const metaNeo = neoJSON!.meta
    const eventNeo = neoJSON ? [...neoJSON.data] : [];
    //! Cuando hay una Hora Extra Activa, Aqui se inserta un evento 'phantom'.
    if(metaNeo.open_overtime){
      const evOvertime = this.setPhantomEvent(metaNeo)
      evOvertime.is_overtime = true; // agergar evento overime
      eventNeo.push(evOvertime)
    }
    const resp:EventsNeo = { meta:metaNeo, data:eventNeo }
    if(serverTime)
      resp.server_time = serverTime;
    return resp
  }

  // Versión V2: procesa respuestas del nuevo API (EventsPayloadV2 con session + meta + data[])
  public eventsDataToNeoV2(events: EventsPayloadV2 | undefined, serverTime?: string): EventsNeo | undefined {
    if (!events) return undefined;
    const filteredEvents = this.filterOptionalScheduleEvents(events);
    const neoJSON = this.fichaJSONbuilderV2.eventsNeo(filteredEvents);
    if (!neoJSON) return undefined;
    const metaNeo = neoJSON.meta;
    const eventNeo = [...neoJSON.data];
    //! Cuando hay una Hora Extra Activa, se inserta un evento 'phantom'.
    if (metaNeo.open_overtime) {
      const evOvertime = this.setPhantomEvent(metaNeo);
      evOvertime.is_overtime = true;
      eventNeo.push(evOvertime);
    }
    const resp: EventsNeo = { meta: metaNeo, data: eventNeo };
    if (serverTime) resp.server_time = serverTime;
    return resp;
  }

  private filterOptionalScheduleEvents(events: EventsPayloadV2): EventsPayloadV2 {
    const opcionales = events.meta?.opcionales;
    const selectedHorarioId = opcionales?.selected;
    if (selectedHorarioId == null) return events;

    return {
      ...events,
      data: events.data.filter((event) => this.shouldKeepEventForSelectedOptionalSchedule(event, selectedHorarioId)),
    };
  }

  private shouldKeepEventForSelectedOptionalSchedule(event: EventDatumV2, selectedHorarioId: number): boolean {
    if (event.type !== 'work') return true;
    if (this.isClockedPhysicalWorkEventWithoutScheduleRule(event)) return true;
    return event.horario_id === selectedHorarioId;
  }

  private isClockedPhysicalWorkEventWithoutScheduleRule(event: EventDatumV2): boolean {
    return typeof event.id === 'number'
      && event.horario_id === 0
      && event.horario_linea_id === 0
      && (event.date_in != null || event.date_out != null);
  }

  public actionEventsToNeo(payload: ActionEventsRefreshPayload | Events, serverTime?: string): EventsNeo | undefined {
    if (this.isActionEventsRefreshPayload(payload) && payload.canonicalEvents) {
      return this.eventsDataToNeoV2(payload.canonicalEvents, serverTime);
    }
    if (this.isActionEventsRefreshPayload(payload)) {
      return this.eventsDataToNeo(payload.legacyEvents, serverTime);
    }
    return this.eventsDataToNeo(payload, serverTime);
  }

  private isActionEventsRefreshPayload(value: ActionEventsRefreshPayload | Events): value is ActionEventsRefreshPayload {
    return !!value && typeof value === 'object' && 'legacyEvents' in value;
  }


  private normalizeEventsPayload(payload: Events | ClockInOutResponse | undefined): Events {
    if (payload && this.isEventsShape(payload)) {
      return payload;
    }

    const wrapped = payload as ClockInOutResponse | undefined;
    if (wrapped?.events && this.isEventsShape(wrapped.events)) {
      return wrapped.events;
    }

    // Fallback defensivo para no romper la cadena de signals si el backend responde incompleto.
    return { meta: {} as Events['meta'], data: [] };
  }

  private isEventsShape(value: unknown): value is Events {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<Events>;
    return !!candidate.meta && Array.isArray(candidate.data);
  }


  // ################################################################
  // ####################### PHANTOM FUNCTIONS #######################

  private setPhantomEvent(meta:MetaNeo){ //usado para crear un evento 'phantom' de hora extra activa
    const event:EventNeo = {...this.eventNeoEmpty.event} // clonar el eventoClean

    const overtime:OpenSubEvent = meta.open_overtime as OpenSubEvent;
    event.type =  'work';
    event.id =    overtime.schedule_id;
    event.event_start_clock_in =            overtime.date_in;
    event.event_start_location_latitud =    overtime.event_start_latitud!;
    event.event_start_location_longitud =   overtime.event_start_longitud!;
    event.event_start_location_map_img =    overtime.event_start_map_img!;
    event.working_hours_expected_overtime = 1;
    event.company_id = meta.company_id || this.settings$()?.userLogged.company_id || 0;

    return event
  }

  public deleteTempIdEvent(id:number){
    const eventsNeo = this.eventsResponse$();
    const eventsNeoTyped = this.eventsNeoTyped$()
    if (!eventsNeoTyped || !eventsNeo) return;

    const index = eventsNeoTyped.findIndex(ev => ev.id === id && ev.eventTypeData.eventTypeId !== 'jornadaFlexible');


    this.setEventsResp(undefined);

    eventsNeo.data.splice(index, 1);
    // insertarlo en el array de events y con esto en todo el flujo
    this.setEventsResp(eventsNeo);
  }

  public setNewTempEvent( type: 'eventExtraTemp'|'overtimeTemp' ){
    const eventsNeo = this.eventsResponse$(); //! ############## DATA VACIO, CREAR UN EVENTO DE CERO
    if (!eventsNeo) return;
    this.setEventsResp(undefined); //limpiar todos los eventos anteriores

    // crear evento dummy con caracteristicas especificas
    const event:EventNeo = {...this.eventNeoEmpty.event} // clonar el eventoClean
    event.type =  'work';
    event.company_id =  this.settings$()?.userLogged.company_id ?? 0; // company_id si existe, usarlo

    if (type == 'eventExtraTemp') {
      event.is_flexible =   true;
      event.type_of_flexible ='total';
    } else if (type == 'overtimeTemp') {
      console.log('setNewTempEvent - overtimeTemp')
      event.working_hours_expected_overtime = 1;
      event.working_hours_expected = 0;
      event.is_overtime = true;
    }

    // insertarlo en el array de events y con esto en todo el flujo, en la funcions de construccion
    // de estados, existe un codigo que al identificar que el evento tiene id=0, marca en proceso.
    eventsNeo.data.push(event);
    this.setEventsResp(eventsNeo);
    // console.log('Ficha.serv - setNewEvent - current this.eventsResponse$() :', this.eventsResponse$())

  }

}
